/**
 * RendicionGastosDetail — CHS Iter 313.j (feb-2026).
 *
 * Página de creación / edición / visualización de una Rendición de Gastos.
 * Sigue el formato del Excel "Formato Rendición ERP" entregado por Renzo:
 *
 *   Cabecera:  RUT + Nombre (auto), Fecha, Folio, Medio de Pago (auto)
 *   Sección 1: Gastos con Boletas y Comprobantes  (TRXB / Bol / GD / CP)
 *              → generará 1 OC agrupada al integrar en Alegra
 *   Sección 2: Facturas y Notas de Crédito         (FE / BH / NC)
 *              → generará 1 OC Express por documento al integrar en Alegra
 *
 * Modos: /nueva  |  /:id  (readonly según estado)  |  /:id/editar
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { validarRutChileno, formatearRut, consultarSII } from '../../lib/rut';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { NumberInput } from '../../components/NumberInput';
import { SearchableCombobox } from '../../components/ui/searchable-combobox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Save, Send, ArrowLeft, Plus, Trash2, Info, CheckCircle, Wallet, Printer, Zap, ExternalLink, Search, Loader2, ChevronRight,
} from 'lucide-react';

const fmtCLP = (n) => `$ ${(n || 0).toLocaleString('es-CL')}`;

const ESTADO_META = {
  borrador:          { label: 'Borrador',      color: 'bg-gray-200 text-gray-700' },
  enviada_vb:        { label: 'Enviada a VB',  color: 'bg-amber-100 text-amber-800' },
  aprobada:          { label: 'Aprobada',      color: 'bg-blue-100 text-blue-800' },
  rechazada:         { label: 'Rechazada',     color: 'bg-red-100 text-red-800' },
  integrada_alegra:  { label: 'Contabilizada', color: 'bg-green-100 text-green-800' },
  modificada:        { label: 'Modificada',    color: 'bg-orange-100 text-orange-800' },
  anulada:           { label: 'Anulada',       color: 'bg-red-200 text-red-900' },
};

const emptyLine = (seccion) => ({
  fecha: new Date().toISOString().substring(0, 10),
  // Iter 313.j.26 (Renzo, feb-2026): Sec.2 default `FE` (era `F`) tras
  // simplificar tipos a FE/BH/NC. El IVA se define en la OC Express al emitir.
  documento_tipo: seccion === 1 ? 'Bol' : 'FE',
  documento_numero: '',
  rut: '',
  rut_nombre: '',
  tipo_gasto_id: '',
  tipo_gasto_nombre: '',
  ot_numero: null,
  detalle: '',
  valor: 0,
  factura_previa: '',
});

// ──────────────── Async combobox para RUT proveedor ────────────────
// Iter 313.j.6 (Renzo, feb-2026): además de guardar en blur:
//   - Valida módulo 11 en tiempo real (indicador visual verde/rojo).
//   - Al hacer blur con RUT válido, normaliza al formato "00000000-A".
//   - Muestra un botón 🔎 que copia el RUT al portapapeles y abre la
//     consulta de situación tributaria en el SII (helper `consultarSII`),
//     el mismo que ya se usa en el Maestro de Proveedores.
//   - Deduplica contra el maestro (backend valida y rechaza duplicados).
const looksLikeRut = (s) => /^[0-9.\-kK]+$/.test((s || '').trim()) && /\d/.test(s || '');

const RutProveedorCombo = ({ value, nombre, onSelect, disabled, testid }) => {
  const [q, setQ] = useState(nombre || value || '');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => { setQ(nombre || value || ''); }, [nombre, value]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQ(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get('/cotizaciones/proveedores/buscar', { params: { q: val, limit: 12 } });
        setResults(r.data || []);
        setOpen(true);
      } catch { setResults([]); }
    }, 250);
  };

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 200);
    const typed = (q || '').trim();
    const currentRut = (value || '').trim();
    const currentNombre = (nombre || '').trim();
    if (!typed) {
      if (currentRut || currentNombre) onSelect({ rut: '', nombre: '' });
      return;
    }
    if (typed !== currentRut && typed !== currentNombre) {
      if (looksLikeRut(typed)) {
        // Iter 313.j.6 — Normalizar al formato 00000000-A si es RUT válido
        const formatted = formatearRut(typed);
        const rutFinal = formatted && validarRutChileno(formatted) ? formatted : typed;
        onSelect({ rut: rutFinal, nombre: currentNombre });
        if (rutFinal !== typed) setQ(rutFinal);
      } else {
        onSelect({ rut: currentRut, nombre: typed });
      }
    }
  };

  // Iter 313.j.6 — Feedback visual de validación módulo 11
  const rutParaValidar = looksLikeRut(q) ? q : value;
  const rutValido = rutParaValidar && looksLikeRut(rutParaValidar) && validarRutChileno(rutParaValidar);
  const rutInvalido = rutParaValidar && looksLikeRut(rutParaValidar) && !rutValido;

  return (
    <div className="relative">
      <div className="flex items-center gap-0.5">
        <Input
          value={q}
          onChange={handleChange}
          placeholder="RUT o razón social"
          className={`h-7 text-xs ${rutInvalido ? 'border-red-400 bg-red-50 dark:bg-red-950/30' : rutValido ? 'border-emerald-400' : ''}`}
          disabled={disabled}
          onBlur={handleBlur}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          data-testid={testid || 'input-rut'}
          title={rutInvalido ? 'RUT inválido (DV módulo 11)' : rutValido ? `RUT válido (${formatearRut(rutParaValidar) || rutParaValidar})` : ''}
        />
        {/* Iter 313.j.6 — botón SII (mismo helper del Maestro de Proveedores) */}
        {looksLikeRut(q) && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); consultarSII(q, toast); }}
            className="h-7 w-7 flex-shrink-0 flex items-center justify-center rounded border border-blue-200 hover:bg-blue-50 text-blue-600"
            title="Validar Razón Social en SII (copia RUT y abre consulta)"
            data-testid={testid ? `${testid}-sii` : 'btn-sii'}
          >
            <Search className="w-3 h-3" />
          </button>
        )}
      </div>
      {rutInvalido && (
        <div className="text-[9px] text-red-600 mt-0.5">RUT inválido (DV no coincide)</div>
      )}
      {open && results.length > 0 && (
        <div className="absolute z-50 top-full left-0 w-72 mt-0.5 bg-white dark:bg-slate-900 border rounded-md shadow-lg max-h-56 overflow-y-auto">
          {results.map((p, i) => (
            <button
              key={`${p.identificacion}-${i}`}
              type="button"
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 dark:hover:bg-blue-950 border-b last:border-0"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect({ rut: p.identificacion || '', nombre: p.nombre || '' });
                setQ(p.nombre || p.identificacion || '');
                setOpen(false);
              }}
            >
              <div className="font-mono font-bold text-blue-700 dark:text-blue-400">{p.identificacion}</div>
              <div className="text-[10px] text-gray-600 dark:text-gray-400 truncate">{p.nombre}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ──────────────── Componente principal ────────────────
export const RendicionGastosDetail = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  // Iter 313.j.5 — El estado editable ahora se decide dinámicamente en `isEditable`
  // según el estado de la rendición y los permisos del user. La URL `/editar`
  // ya no es necesaria.

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [fondo, setFondo] = useState(null);
  const [fondosDisponibles, setFondosDisponibles] = useState([]);  // Solo para gerencia
  const [fondoSeleccionado, setFondoSeleccionado] = useState(''); // codigo del fondo elegido por gerencia
  const [rendicion, setRendicion] = useState(null);
  const [otsList, setOtsList] = useState([]);
  const [cuentasGasto, setCuentasGasto] = useState([]);
  const [tiposDoc, setTiposDoc] = useState({ seccion1: [], seccion2: [] });

  // Estado editable
  const [fecha, setFecha] = useState(new Date().toISOString().substring(0, 10));
  const [seccion1, setSeccion1] = useState([]);
  const [seccion2, setSeccion2] = useState([]);
  const [observaciones, setObservaciones] = useState('');

  // Iter 313.j.8 (Renzo, feb-2026) — Modal OC Express Sección 2.
  // Se abre al hacer clic en "Emitir OC" en cada fila de la S2. El usuario
  // ajusta OT, tipo de gasto, detalle y valor ANTES de confirmar la creación
  // de la OC. Reemplaza el POST directo del iter 313.j.4.
  const [ocExpressModal, setOcExpressModal] = useState({
    open: false,
    lineIdx: null,
    data: null,  // copia editable de la línea
  });

  // Iter 313.j.14 (Renzo, feb-2026) — Modal Buscar Material del maestro.
  // Se abre desde el ícono `⌕` en la columna Detalle de cada línea (S1 y S2).
  // Al elegir un material, autocompleta `detalle` y persiste `codigo_material`
  // en la línea. NO autocompleta valor ni tipo_gasto (Renzo lo elige manual).
  const [materialModal, setMaterialModal] = useState({
    open: false,
    section: null,   // 's1' | 's2'
    lineIdx: null,
  });
  const [materialQuery, setMaterialQuery] = useState('');
  const [materialResults, setMaterialResults] = useState([]);
  const [materialLoading, setMaterialLoading] = useState(false);

  // Iter 313.j.25 (Renzo, feb-2026) — Eliminación de rendición desde el detail.
  // Restringido a admin/gerencia + todas las OCs anuladas (backend valida).
  const [puedeEliminar, setPuedeEliminar] = useState({ puede_eliminar: false, puede_editar: false, motivo: '', ocs_no_anuladas: [], ocs_por_linea: [] });
  const [eliminando, setEliminando] = useState(false);
  const [confirmEliminar, setConfirmEliminar] = useState(false);
  // Iter 313.j.27 (Renzo, feb-2026) — Contabilización en Alegra
  const [integrando, setIntegrando] = useState(false);
  const [integracionResult, setIntegracionResult] = useState(null);
  const [confirmIntegrar, setConfirmIntegrar] = useState(false);
  // Iter 313.j.33 (Renzo, feb-2026) — Precheck Alegra (proveedores + cuentas)
  const [precheckResult, setPrecheckResult] = useState(null);
  const [precheckLoading, setPrecheckLoading] = useState(false);
  // Iter 313.j.33 — Historial de operaciones Alegra
  const [alegraHistorial, setAlegraHistorial] = useState([]);
  // Iter 313.j.31 (Renzo, feb-2026) — Modo edición supervisado (admin/gerencia)
  const [modoSupervisado, setModoSupervisado] = useState(false);
  // Iter 313.j.38 (Renzo, feb-2026) — Aprobar/Rechazar desde el detalle
  //   * `confirmAprobarDialog`: dialog VB (admin/gerencia).
  //   * `contabilizarTrasAprobar`: checkbox → tras VB, encadenar precheck + integrar.
  //   * `aprobando/rechazando`: locks anti-doble-click.
  //   * `confirmRechazarDialog` + `motivoRechazo`: textarea de motivo.
  const [confirmAprobarDialog, setConfirmAprobarDialog] = useState(false);
  const [contabilizarTrasAprobar, setContabilizarTrasAprobar] = useState(true);
  const [aprobando, setAprobando] = useState(false);
  const [confirmRechazarDialog, setConfirmRechazarDialog] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [rechazando, setRechazando] = useState(false);

  const isGerencia = user?.rol === 'admin' || user?.rol === 'gerencia' || user?.rol === 'contabilidad';

  // ─────── Cargar catálogos (OTs abiertas + cuentas gasto + tipos doc) ───────
  useEffect(() => {
    (async () => {
      try {
        const [otsR, catR, tdR] = await Promise.all([
          // Iter 313.j.4 — include_all para que aparezcan también las OTs < 1000
          api.get('/ordenes-trabajo', { params: { estado: 'EN EJECUCION', limit: 500, include_all: true } }).catch(() => ({ data: [] })),
          api.get('/contabilidad/cuentas-alegra/categorias').catch(() => ({ data: [] })),
          api.get('/rendiciones-gastos/config/tipos-documento').catch(() => ({ data: {} })),
        ]);
        // OTs
        const otsArr = Array.isArray(otsR.data) ? otsR.data : (otsR.data?.ordenes_trabajo || []);
        setOtsList(otsArr);
        // Cuentas de gasto (expense)
        let cats = Array.isArray(catR.data) ? catR.data : (catR.data?.categorias || []);
        cats = cats.filter((c) => (c.type || '').toLowerCase() === 'expense' || !c.type);
        setCuentasGasto(cats);
        setTiposDoc(tdR.data || {});
      } catch (err) {
        console.error('Error cargando catálogos:', err);
      }
    })();
  }, []);

  // ─────── Cargar fondo del usuario ───────
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/rendiciones-gastos/mi-fondo');
        setFondo(r.data);
      } catch {
        setFondo(null);
      }
    })();
  }, []);

  // ─────── Si es Gerencia, cargar lista de todos los fondos (para crear en nombre de otro) ───────
  useEffect(() => {
    if (!isGerencia || !isNew) return;
    (async () => {
      try {
        const r = await api.get('/rendiciones-gastos/config/fondos');
        setFondosDisponibles(r.data || []);
      } catch (err) {
        console.error('Error cargando lista de fondos:', err);
      }
    })();
  }, [isGerencia, isNew]);

  // ─────── Cargar rendición existente ───────
  const loadRendicion = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const r = await api.get(`/rendiciones-gastos/${id}`);
      setRendicion(r.data);
      setFecha(r.data.fecha);
      setSeccion1(r.data.seccion1_lines || []);
      setSeccion2(r.data.seccion2_lines || []);
      setObservaciones(r.data.observaciones || '');
    } catch (err) {
      toast.error('Error al cargar rendición', { description: err?.response?.data?.detail });
      navigate('/compras/rendiciones-gastos');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, navigate]);

  useEffect(() => { loadRendicion(); }, [loadRendicion]);

  // Iter 313.j.25 — cargar permisos de eliminación (solo admin/gerencia).
  // El backend responde `puede_eliminar` según estado + estado de OCs asociadas.
  useEffect(() => {
    if (isNew || !id) return;
    const isAdminGer = user?.rol === 'admin' || user?.rol === 'gerencia';
    if (!isAdminGer) { setPuedeEliminar({ puede_eliminar: false, motivo: '', ocs_no_anuladas: [] }); return; }
    (async () => {
      try {
        const r = await api.get(`/rendiciones-gastos/${id}/puede-eliminar`);
        setPuedeEliminar(r.data || { puede_eliminar: false, puede_editar: false, motivo: '', ocs_no_anuladas: [], ocs_por_linea: [] });
      } catch {
        setPuedeEliminar({ puede_eliminar: false, puede_editar: false, motivo: '', ocs_no_anuladas: [], ocs_por_linea: [] });
      }
    })();
  }, [id, isNew, rendicion?.estado, user?.rol]);

  const handleEliminarRendicion = async () => {
    if (!id) return;
    setEliminando(true);
    try {
      await api.delete(`/rendiciones-gastos/${id}`);
      toast.success(`Rendición ${rendicion?.folio || ''} eliminada`);
      navigate('/compras/rendiciones-gastos');
    } catch (err) {
      toast.error('Error al eliminar', { description: err?.response?.data?.detail });
    } finally {
      setEliminando(false);
      setConfirmEliminar(false);
    }
  };

  // Iter 313.j.27 — Contabilizar la Rendición en Alegra
  const handleIntegrarAlegra = async () => {
    if (!id) return;
    setIntegrando(true);
    try {
      const r = await api.post(`/rendiciones-gastos/${id}/integrar-alegra`);
      setIntegracionResult(r.data);
      const errs = (r.data?.errores || []).length;
      if (errs === 0) {
        toast.success('Rendición contabilizada en Alegra');
        loadRendicion();
        loadAlegraHistorial();
      } else {
        toast.warning(`Contabilización parcial: ${errs} error(es)`, { description: 'Revisa el detalle abajo' });
      }
    } catch (err) {
      toast.error('Error al contabilizar', { description: err?.response?.data?.detail });
    } finally {
      setIntegrando(false);
      setConfirmIntegrar(false);
    }
  };

  // Iter 313.j.33 — Precheck ANTES de abrir el diálogo de contabilizar
  const handleAbrirContabilizar = async () => {
    if (!id) return;
    setPrecheckLoading(true);
    try {
      const r = await api.get(`/rendiciones-gastos/${id}/precheck-alegra`);
      setPrecheckResult(r.data);
      if (r.data?.puede_contabilizar) {
        setConfirmIntegrar(true);
      }
    } catch (err) {
      toast.error('Error en precheck', { description: err?.response?.data?.detail });
    } finally {
      setPrecheckLoading(false);
    }
  };

  // Iter 313.j.38 (Renzo, feb-2026) — Aprobar Rendición desde el detalle
  //   * Restringido a admin/gerencia.
  //   * Encadenamiento opcional: si `contabilizarTrasAprobar=true`, tras `POST /aprobar`
  //     ejecutamos precheck + integrar-alegra sin rebotar al usuario a otra pantalla.
  //   * Si el precheck falla o Alegra rechaza, se muestra el resultado (`precheckResult`
  //     o `integracionResult`) y la rendición queda en `aprobada` para reintento manual.
  const handleAprobarDetalle = async () => {
    if (!id) return;
    setAprobando(true);
    try {
      await api.post(`/rendiciones-gastos/${id}/aprobar`);
      toast.success('Rendición aprobada');
      await loadRendicion();
      setConfirmAprobarDialog(false);

      if (contabilizarTrasAprobar) {
        // Encadenar: precheck → integrar
        setPrecheckLoading(true);
        let precheck;
        try {
          const rp = await api.get(`/rendiciones-gastos/${id}/precheck-alegra`);
          precheck = rp.data;
          setPrecheckResult(precheck);
        } catch (err) {
          toast.error('Aprobada pero falló el precheck', { description: err?.response?.data?.detail });
          return;
        } finally {
          setPrecheckLoading(false);
        }
        if (!precheck?.puede_contabilizar) {
          toast.warning('Aprobada. Precheck detectó problemas: revisa el panel abajo.');
          setConfirmIntegrar(true);  // Abre el diálogo con el detalle del precheck
          return;
        }
        // Integrar
        setIntegrando(true);
        try {
          const ri = await api.post(`/rendiciones-gastos/${id}/integrar-alegra`);
          setIntegracionResult(ri.data);
          const errs = (ri.data?.errores || []).length;
          if (errs === 0) {
            toast.success('Rendición aprobada y contabilizada en Alegra');
          } else {
            toast.warning(`Aprobada. Contabilización parcial: ${errs} error(es)`);
          }
          await loadRendicion();
          await loadAlegraHistorial();
        } catch (err) {
          toast.error('Aprobada pero falló Alegra', { description: err?.response?.data?.detail });
        } finally {
          setIntegrando(false);
        }
      }
    } catch (err) {
      toast.error('Error al aprobar', { description: err?.response?.data?.detail });
    } finally {
      setAprobando(false);
    }
  };

  // Iter 313.j.38 (Renzo, feb-2026) — Rechazar Rendición desde el detalle
  const handleRechazarDetalle = async () => {
    if (!id) return;
    const motivo = (motivoRechazo || '').trim();
    if (!motivo) {
      toast.error('El motivo de rechazo es obligatorio');
      return;
    }
    setRechazando(true);
    try {
      await api.post(`/rendiciones-gastos/${id}/rechazar`, { motivo });
      toast.success('Rendición rechazada. El rendidor puede editarla y re-enviarla.');
      setConfirmRechazarDialog(false);
      setMotivoRechazo('');
      await loadRendicion();
    } catch (err) {
      toast.error('Error al rechazar', { description: err?.response?.data?.detail });
    } finally {
      setRechazando(false);
    }
  };

  // Iter 313.j.33 — Historial de operaciones Alegra
  const loadAlegraHistorial = useCallback(async () => {
    if (!id || isNew) return;
    try {
      const r = await api.get(`/rendiciones-gastos/${id}/alegra-historial`);
      setAlegraHistorial(r.data?.operations || []);
    } catch {
      setAlegraHistorial([]);
    }
  }, [id, isNew]);

  // Iter 313.j.33 — cargar historial Alegra al montar/refrescar (después de definir loadAlegraHistorial para evitar TDZ)
  useEffect(() => { loadAlegraHistorial(); }, [loadAlegraHistorial]);

  // Iter 313.j.38 (Renzo, feb-2026) — auto-abrir el diálogo Contabilizar cuando
  // el listado redirige con `?auto_contabilizar=1` tras la aprobación.
  const autoContabilizarDisparado = useRef(false);
  useEffect(() => {
    if (autoContabilizarDisparado.current) return;
    if (isNew || !rendicion) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('auto_contabilizar') !== '1') return;
    if (rendicion.estado !== 'aprobada') return;
    if (!(user?.rol === 'admin' || user?.rol === 'gerencia')) return;
    autoContabilizarDisparado.current = true;
    handleAbrirContabilizar();
    // Limpiar query param sin recargar
    const url = new URL(window.location.href);
    url.searchParams.delete('auto_contabilizar');
    window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
  }, [rendicion, isNew, user?.rol]);

  // ─────── Estado readonly según estado del doc ───────
  // Iter 313.j.5 (Renzo, feb-2026): si la rendición está en borrador/rechazada
  // y el user tiene permiso, activar edición inline sin necesidad de estar en
  // `/editar`. Simplifica el flujo de prueba/uso.
  const isEditable = useMemo(() => {
    if (isNew) return true;
    const est = rendicion?.estado;
    if (est === 'borrador' || est === 'rechazada') {
      const isOwner = rendicion?.rendidor?.user_email === user?.email;
      return isOwner || isGerencia;
    }
    // Iter 313.j.31 — Modo supervisado: admin/gerencia puede editar cuando lo activa
    // explícitamente y el backend valida por línea que la OC esté anulada.
    if (modoSupervisado && (user?.rol === 'admin' || user?.rol === 'gerencia')) return true;
    return false;
  }, [isNew, rendicion, user, isGerencia, modoSupervisado]);

  const readonly = !isEditable;

  // ─────── Cálculo de totales en vivo ───────
  const totales = useMemo(() => {
    const t1 = seccion1.reduce((sum, l) => sum + (parseInt(l.valor) || 0), 0);
    const t2 = seccion2.reduce((sum, l) => sum + (parseInt(l.valor) || 0), 0);
    return { total_seccion1: t1, total_seccion2: t2, total_general: t1 + t2 };
  }, [seccion1, seccion2]);

  // ─────── Handlers de líneas ───────
  const updateLine = (seccion, idx, field, value) => {
    const setter = seccion === 1 ? setSeccion1 : setSeccion2;
    setter((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const addLine = (seccion) => {
    const setter = seccion === 1 ? setSeccion1 : setSeccion2;
    setter((prev) => [...prev, emptyLine(seccion)]);
  };

  const removeLine = (seccion, idx) => {
    const setter = seccion === 1 ? setSeccion1 : setSeccion2;
    setter((prev) => prev.filter((_, i) => i !== idx));
  };

  // ─────── Guardar / Actualizar / Enviar VB ───────
  const buildPayload = () => ({
    fecha,
    // Iter 313.j.5 — mantenemos `rut_nombre` en el payload (el backend lo usa
    // para auto-crear el proveedor si no existe en el maestro).
    seccion1_lines: seccion1,
    seccion2_lines: seccion2,
    observaciones,
    // Iter 313.j.3: Gerencia puede crear en nombre de otro rendidor
    fondo_codigo: (isNew && isGerencia && !fondo && fondoSeleccionado) ? fondoSeleccionado : undefined,
  });

  const handleGuardar = async () => {
    if (seccion1.length === 0 && seccion2.length === 0) {
      toast.error('Debe agregar al menos una línea de gasto');
      return;
    }
    // Si es Gerencia sin fondo propio, exigir que haya elegido un fondo
    if (isNew && isGerencia && !fondo && !fondoSeleccionado) {
      toast.error('Selecciona el funcionario/fondo para el cual estás creando la rendición');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const r = await api.post('/rendiciones-gastos', buildPayload());
        toast.success(`Rendición ${r.data.folio} creada como borrador`);
        navigate(`/compras/rendiciones-gastos/${r.data.id}`);
      } else {
        await api.put(`/rendiciones-gastos/${id}`, buildPayload());
        toast.success('Rendición actualizada');
        loadRendicion();
      }
    } catch (err) {
      toast.error('Error al guardar', { description: err?.response?.data?.detail });
    } finally {
      setSaving(false);
    }
  };

  const handleEnviarVB = async () => {
    if (!window.confirm(`¿Enviar rendición ${rendicion?.folio} a VB de Gerencia/Administrador? No podrás editarla hasta que sea aprobada o rechazada.`)) return;
    setSaving(true);
    try {
      // Si hay cambios sin guardar → guardar primero
      await api.put(`/rendiciones-gastos/${id}`, buildPayload());
      await api.post(`/rendiciones-gastos/${id}/enviar-vb`);
      toast.success('Rendición enviada a VB de Gerencia');
      navigate('/compras/rendiciones-gastos');
    } catch (err) {
      toast.error('Error', { description: err?.response?.data?.detail });
    } finally {
      setSaving(false);
    }
  };

  // ─────── Comboboxes ───────
  const otItems = useMemo(
    () => otsList.map((ot) => ({
      value: String(ot.numero_ot),
      label: `OT-${ot.numero_ot}`,
      subLabel: (ot.descripcion || '').substring(0, 60),
    })),
    [otsList]
  );

  const cuentasItems = useMemo(
    () => cuentasGasto.map((c) => ({
      value: String(c.id),
      label: c.name || `#${c.id}`,
      subLabel: c.code ? `Cta ${c.code}` : '',
    })),
    [cuentasGasto]
  );

  // Iter 313.j.9 — cálculos derivados del modal según tipo_oc (memoizado antes
  // de cualquier early return para respetar las reglas de hooks).
  const modalTotales = useMemo(() => {
    const d = ocExpressModal.data;
    if (!d) return { neto: 0, iva: 0, retencion: 0, otros: 0, neto_exento: 0, total: 0 };
    const valor = parseInt(d.valor) || 0;
    const ivaPct = Number(d.iva_porcentaje) || 0;
    const retPct = Number(d.retencion_porcentaje) || 0;
    const netoExento = parseInt(d.neto_exento) || 0;
    const otros = (d.otros_impuestos || []).reduce((s, oi) => s + (parseInt(oi.monto) || 0), 0);
    let neto = 0, iva = 0, retencion = 0, total = valor;
    if (d.tipo_oc === 'afecta') {
      neto = ivaPct > 0 ? Math.round(valor / (1 + ivaPct / 100)) : valor;
      iva = valor - neto;
      total = valor;
    } else if (d.tipo_oc === 'honorarios') {
      neto = valor;
      retencion = Math.round(valor * retPct / 100);
      total = valor - retencion;
    } else if (d.tipo_oc === 'combustible') {
      const base = Math.max(0, valor - otros);
      neto = ivaPct > 0 ? Math.round(base / (1 + ivaPct / 100)) : base;
      iva = base - neto;
      total = valor;
    } else if (d.tipo_oc === 'exenta') {
      neto = valor;
      total = valor;
    } else if (d.tipo_oc === 'mixta') {
      neto = valor;
      iva = ivaPct > 0 ? Math.round(neto * ivaPct / 100) : 0;
      total = neto + iva + netoExento;
    }
    return { neto, iva, retencion, otros, neto_exento: netoExento, total };
  }, [ocExpressModal.data]);

  // Iter 313.j.14 — debounce búsqueda de material (declarado antes de early returns)
  useEffect(() => {
    if (!materialModal.open) return;
    const q = (materialQuery || '').trim();
    if (q.length < 2) { setMaterialResults([]); return; }
    const t = setTimeout(async () => {
      setMaterialLoading(true);
      try {
        const r = await api.get('/compras/materiales', { params: { q, limit: 30 } });
        setMaterialResults(r.data?.materiales || []);
      } catch (err) {
        setMaterialResults([]);
      } finally {
        setMaterialLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [materialQuery, materialModal.open]);

  // ─────── Render ───────
  if (loading) return <div className="p-8 text-center text-gray-500">Cargando rendición…</div>;

  // Nueva rendición pero sin fondo → bloquear (solo si NO es Gerencia; Gerencia puede crear en nombre de otro)
  if (isNew && !fondo && !isGerencia) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-300 rounded-lg p-6 text-center">
          <Wallet className="w-12 h-12 text-amber-600 mx-auto mb-3" />
          <h2 className="text-lg font-bold mb-2">Sin fondo por rendir</h2>
          <p className="text-sm mb-4">
            No tienes un fondo por rendir asociado. Contacta a Contabilidad para configurarlo.
          </p>
          <Button variant="outline" onClick={() => navigate('/compras/rendiciones-gastos')}>Volver</Button>
        </div>
      </div>
    );
  }

  // Metadatos del fondo elegido (por Gerencia) — se muestran como si fuera "el fondo propio"
  const fondoActivo = fondo || (isNew && isGerencia && fondoSeleccionado
    ? fondosDisponibles.find((f) => f.codigo === fondoSeleccionado)
    : null);

  const meta = ESTADO_META[rendicion?.estado] || null;
  // Iter 313.j.3 — Gerencia puede crear en nombre de otro rendidor
  const isCreacionEnNombreDeOtro = isNew && isGerencia && !fondo;
  const rendidorNombre = rendicion?.rendidor?.nombre || fondoActivo?.persona_asociada || user?.nombre;
  const medioPagoNombre = rendicion?.medio_pago_nombre || fondoActivo?.nombre || '-';
  // Iter 313.j.4 — RUT del rendidor: prioridad al doc guardado, luego fondo.rut_persona,
  // luego user.rut. El botón "OC Express" en Alegra usa este RUT como contacto.
  const rendidorRut = rendicion?.rendidor?.rut
    || fondoActivo?.rut_persona
    || (fondoActivo ? '' : user?.rut)
    || '';

  const canEnviarVB = !isNew && rendicion?.estado === 'borrador' && rendicion?.rendidor?.user_email === user?.email;

  const handleImprimirPdf = () => {
    if (!id) return;
    // Abrir el PDF en nueva pestaña; el backend devuelve inline con Content-Disposition
    const base = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${base}/api/rendiciones-gastos/${id}/pdf`, '_blank');
  };

  // Iter 313.j.4 — Emitir OC Express agrupada para Sección 1
  const handleEmitirOCSeccion1 = async () => {
    if (isNew) {
      toast.error('Guarda la rendición como borrador antes de emitir OC Express');
      return;
    }
    if (rendicion?.seccion1_oc_numero) {
      toast.error(`Esta sección ya tiene OC ${rendicion.seccion1_oc_numero} emitida`);
      return;
    }
    // Validar tempranamente en el cliente para feedback rápido
    if (seccion1.length === 0) {
      toast.error('Agrega al menos una línea a Sección 1');
      return;
    }
    for (let i = 0; i < seccion1.length; i++) {
      const l = seccion1[i];
      if (!l.tipo_gasto_id && !l.tipo_gasto_nombre) return toast.error(`Línea ${i+1}: Tipo de Gasto es obligatorio`);
      if (!l.ot_numero) return toast.error(`Línea ${i+1}: OT es obligatoria`);
      if (!(l.detalle || '').trim()) return toast.error(`Línea ${i+1}: Detalle es obligatorio`);
      if (!(parseInt(l.valor) > 0)) return toast.error(`Línea ${i+1}: Monto debe ser mayor que 0`);
    }
    if (!window.confirm(`¿Emitir 1 OC Express agrupada por ${fmtCLP(totales.total_seccion1)} para los ${seccion1.length} items de Sección 1?`)) return;
    setSaving(true);
    try {
      // Guardar cambios antes
      await api.put(`/rendiciones-gastos/${id}`, buildPayload());
      const r = await api.post(`/rendiciones-gastos/${id}/emitir-oc-seccion1`);
      toast.success(`OC ${r.data.numero_oc} emitida por ${fmtCLP(r.data.total)}`);
      loadRendicion();
    } catch (err) {
      toast.error('Error al emitir OC', { description: err?.response?.data?.detail });
    } finally {
      setSaving(false);
    }
  };

  // Iter 313.j.4 — Emitir OC Express individual para una línea de Sección 2
  // Iter 313.j.8 (Renzo, feb-2026) — Ahora abre el modal en vez de crear la OC
  // directamente. El usuario ajusta OT, tipo de gasto, detalle y valor antes.
  // Iter 313.j.9 (Renzo, feb-2026) — El modal replica los 5 tipos de OC del
  // generador normal (afecta / honorarios / combustible / exenta / mixta).
  const handleEmitirOCSeccion2 = async (lineIdx) => {
    if (isNew) {
      toast.error('Guarda la rendición como borrador antes de emitir OC Express');
      return;
    }
    const l = seccion2[lineIdx];
    if (l.oc_numero) return toast.error(`Esta línea ya tiene OC ${l.oc_numero} emitida`);
    if (l.documento_tipo === 'NC') return toast.error('Las NC no requieren OC — se cruzan contra factura previa');
    if (!(l.rut || '').trim()) return toast.error('RUT/Proveedor es obligatorio (completa la línea antes de emitir la OC)');
    // Guardar los cambios pendientes de la rendición para que el backend
    // trabaje contra la versión más reciente.
    try {
      await api.put(`/rendiciones-gastos/${id}`, buildPayload());
    } catch (err) {
      toast.error('Error al guardar cambios previos', { description: err?.response?.data?.detail });
      return;
    }
    // Abrir modal con copia editable de la línea + defaults del tipo_oc
    setOcExpressModal({
      open: true,
      lineIdx,
      data: {
        fecha: l.fecha || fecha,
        documento_tipo: l.documento_tipo || 'FE',
        documento_numero: l.documento_numero || '',
        rut: l.rut || '',
        rut_nombre: l.rut_nombre || '',
        ot_numero: l.ot_numero ? String(l.ot_numero) : '',
        tipo_gasto_id: l.tipo_gasto_id || '',
        tipo_gasto_nombre: l.tipo_gasto_nombre || '',
        detalle: l.detalle || '',
        valor: parseInt(l.valor) || 0,
        // Iter 313.j.9 — tipo de OC + impuestos
        tipo_oc: l.tipo_oc || 'afecta',
        iva_porcentaje: l.iva_porcentaje != null ? Number(l.iva_porcentaje) : 19,
        retencion_porcentaje: l.retencion_porcentaje != null ? Number(l.retencion_porcentaje) : 15.25,
        neto_exento: parseInt(l.neto_exento) || 0,
        otros_impuestos: Array.isArray(l.otros_impuestos) ? l.otros_impuestos : [],
      },
    });
  };

  // Iter 313.j.8 — Confirmación del modal: aplica overrides y crea la OC Express
  const confirmarEmitirOCSeccion2 = async () => {
    const { lineIdx, data } = ocExpressModal;
    if (lineIdx == null || !data) return;
    // Validaciones cliente-side (mismo criterio que el backend)
    if (!(data.rut || '').trim()) return toast.error('RUT/Proveedor es obligatorio');
    if (!(data.documento_numero || '').trim()) return toast.error('Nº de documento es obligatorio');
    if (!(data.detalle || '').trim()) return toast.error('Detalle es obligatorio');
    if (!(data.ot_numero || '').toString().trim()) return toast.error('OT es obligatoria');
    if (!(data.tipo_gasto_id || data.tipo_gasto_nombre || '').trim()) return toast.error('Tipo de Gasto es obligatorio');
    if (!(parseInt(data.valor) > 0)) return toast.error('Valor debe ser mayor que 0');
    if (data.tipo_oc === 'mixta' && !(parseInt(data.neto_exento) > 0)) {
      return toast.error('Factura Mixta: ingresa el Neto Exento (> 0) o cambia el tipo de OC');
    }
    setSaving(true);
    try {
      const body = {
        fecha: data.fecha,
        documento_tipo: data.documento_tipo,
        documento_numero: data.documento_numero,
        rut: data.rut,
        rut_nombre: data.rut_nombre,
        tipo_gasto_id: data.tipo_gasto_id,
        tipo_gasto_nombre: data.tipo_gasto_nombre,
        ot_numero: data.ot_numero ? parseInt(data.ot_numero) : null,
        detalle: data.detalle,
        valor: parseInt(data.valor) || 0,
        // Iter 313.j.9 — 5 tipos + impuestos
        tipo_oc: data.tipo_oc || 'afecta',
        iva_porcentaje: Number(data.iva_porcentaje) || 0,
        retencion_porcentaje: Number(data.retencion_porcentaje) || 0,
        neto_exento: parseInt(data.neto_exento) || 0,
        otros_impuestos: (data.otros_impuestos || []).filter((oi) => (oi.nombre || '').trim() && parseInt(oi.monto) > 0),
      };
      const r = await api.post(`/rendiciones-gastos/${id}/emitir-oc-seccion2/${lineIdx}`, body);
      toast.success(`OC ${r.data.numero_oc} emitida por ${fmtCLP(r.data.total)} (${r.data.tipo_oc || 'afecta'} · Contado)`);
      setOcExpressModal({ open: false, lineIdx: null, data: null });
      loadRendicion();
    } catch (err) {
      toast.error('Error al emitir OC', { description: err?.response?.data?.detail });
    } finally {
      setSaving(false);
    }
  };

  const updateModalField = (field, value) => {
    setOcExpressModal((prev) => ({ ...prev, data: { ...prev.data, [field]: value } }));
  };

  // Iter 313.j.9 — helpers para manejar otros_impuestos dentro del modal
  const addOtroImpuesto = () => {
    setOcExpressModal((prev) => ({
      ...prev,
      data: { ...prev.data, otros_impuestos: [...(prev.data.otros_impuestos || []), { nombre: '', monto: 0 }] },
    }));
  };
  const updateOtroImpuesto = (idx, field, value) => {
    setOcExpressModal((prev) => {
      const arr = [...(prev.data.otros_impuestos || [])];
      arr[idx] = { ...arr[idx], [field]: field === 'monto' ? (parseInt(value) || 0) : value };
      return { ...prev, data: { ...prev.data, otros_impuestos: arr } };
    });
  };
  const removeOtroImpuesto = (idx) => {
    setOcExpressModal((prev) => ({
      ...prev,
      data: { ...prev.data, otros_impuestos: (prev.data.otros_impuestos || []).filter((_, i) => i !== idx) },
    }));
  };

  // ── Iter 313.j.14 — Búsqueda de materiales del maestro ──
  const openMaterialSearch = (section, lineIdx) => {
    setMaterialQuery('');
    setMaterialResults([]);
    setMaterialModal({ open: true, section, lineIdx });
  };
  const closeMaterialModal = () => setMaterialModal({ open: false, section: null, lineIdx: null });
  const selectMaterial = (m) => {
    const { section, lineIdx } = materialModal;
    if (section == null || lineIdx == null || !m) return closeMaterialModal();
    const desc = (m.descripcion2 || m.descripcion || '').trim();
    const updater = section === 's1' ? setSeccion1 : setSeccion2;
    updater((prev) => prev.map((l, i) => (
      i === lineIdx ? { ...l, detalle: desc, codigo_material: m.codigo || '' } : l
    )));
    toast.success(`Material asociado: ${desc.substring(0, 50)}`);
    closeMaterialModal();
  };

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-7xl mx-auto" data-testid="rendicion-detail-page">
      {/* Encabezado */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/compras/rendiciones-gastos')} data-testid="btn-volver">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver al listado
          </Button>
          <h1 className="text-2xl font-bold mt-1">
            {isNew ? 'Nueva Rendición de Gastos' : `Rendición Nº ${rendicion?.folio}`}
          </h1>
          {meta && (
            <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${meta.color}`}>
              {meta.label}
            </span>
          )}
          {rendicion?.estado === 'rechazada' && rendicion?.motivo_rechazo && (
            <div className="mt-2 text-xs bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded p-2 max-w-lg">
              <strong>Motivo de rechazo:</strong> {rendicion.motivo_rechazo}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Button variant="outline" onClick={handleImprimirPdf} data-testid="btn-imprimir-pdf">
              <Printer className="w-4 h-4 mr-1" /> Imprimir PDF
            </Button>
          )}
          {isEditable && (
            <Button onClick={handleGuardar} disabled={saving} data-testid="btn-guardar">
              <Save className="w-4 h-4 mr-1" /> {isNew ? 'Crear Borrador' : 'Guardar Cambios'}
            </Button>
          )}
          {canEnviarVB && (
            <Button onClick={handleEnviarVB} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-enviar-vb">
              <Send className="w-4 h-4 mr-1" /> Enviar a VB Gerencia/Administrador
            </Button>
          )}
          {/* Iter 313.j.38 (Renzo, feb-2026) — VB desde el detalle (admin/gerencia). */}
          {/* Iter 313.j.39: si estado=borrador y puedeEliminar.puede_aprobar_directo=true (todas las OCs aprobadas), */}
          {/* también se muestra el botón para que admin autorice directamente saltando el paso 'enviar a VB'. */}
          {!isNew && (rendicion?.estado === 'enviada_vb' ||
                      (rendicion?.estado === 'borrador' && puedeEliminar.puede_aprobar_directo)) &&
                     (user?.rol === 'admin' || user?.rol === 'gerencia') && (
            <>
              <Button
                onClick={() => { setContabilizarTrasAprobar(true); setConfirmAprobarDialog(true); }}
                disabled={aprobando || rechazando}
                className="bg-green-600 hover:bg-green-700 text-white"
                title={puedeEliminar.motivo_aprobar || 'Autorizar la rendición'}
                data-testid="btn-aprobar-rendicion"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                {rendicion?.estado === 'borrador' ? 'Autorizar y Contabilizar' : 'Aprobar (VB)'}
              </Button>
              {rendicion?.estado === 'enviada_vb' && (
                <Button
                  variant="destructive"
                  onClick={() => { setMotivoRechazo(''); setConfirmRechazarDialog(true); }}
                  disabled={aprobando || rechazando}
                  data-testid="btn-rechazar-rendicion"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Rechazar
                </Button>
              )}
            </>
          )}
          {/* Iter 313.j.39 — Info: admin ve por qué no puede autorizar aún (OCs pendientes) */}
          {!isNew && rendicion?.estado === 'borrador' && !puedeEliminar.puede_aprobar_directo &&
                     (user?.rol === 'admin' || user?.rol === 'gerencia') &&
                     puedeEliminar.motivo_aprobar && (puedeEliminar.ocs_pendientes_vb?.length > 0) && (
            <div
              className="text-xs text-amber-700 bg-amber-50 border border-amber-300 rounded px-2 py-1 max-w-md"
              data-testid="hint-autorizar-oc-pendientes"
              title={puedeEliminar.motivo_aprobar}
            >
              <Info className="w-3 h-3 inline mr-1" />
              {(puedeEliminar.ocs_pendientes_vb || []).length === 0
                ? puedeEliminar.motivo_aprobar
                : `Falta VB en ${puedeEliminar.ocs_pendientes_vb.length} OC(s) para poder autorizar`}
            </div>
          )}
          {/* Iter 313.j.27 (Renzo, feb-2026) — Contabilizar en Alegra (aprobada → integrada_alegra) */}
          {!isNew && rendicion?.estado === 'aprobada' && (user?.rol === 'admin' || user?.rol === 'gerencia') && (
            <Button
              onClick={handleAbrirContabilizar}
              disabled={integrando || precheckLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-integrar-alegra"
            >
              {precheckLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
              Contabilizar en Alegra
            </Button>
          )}
          {/* Iter 313.j.31 (Renzo, feb-2026) — Editar Rendición supervisado (admin/gerencia) */}
          {!isNew && rendicion?.estado !== 'borrador' && rendicion?.estado !== 'rechazada' && puedeEliminar.puede_editar && (
            <Button
              onClick={() => setModoSupervisado(v => !v)}
              variant={modoSupervisado ? 'default' : 'outline'}
              className={modoSupervisado ? 'bg-orange-600 hover:bg-orange-700 text-white' : ''}
              data-testid="btn-editar-rendicion"
            >
              <Save className="w-4 h-4 mr-1" /> {modoSupervisado ? 'Salir Edición' : 'Editar Rendición'}
            </Button>
          )}
          {/* Iter 313.j.25 (Renzo, feb-2026) — Eliminar rendición (admin/gerencia + OCs anuladas) */}
          {!isNew && (user?.rol === 'admin' || user?.rol === 'gerencia') && (
            <Button
              variant="destructive"
              onClick={() => setConfirmEliminar(true)}
              disabled={eliminando}
              title={puedeEliminar.puede_eliminar ? 'Eliminar esta rendición' : (puedeEliminar.motivo || 'No disponible')}
              data-testid="btn-eliminar-rendicion"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Eliminar Rendición
            </Button>
          )}
        </div>
      </div>

      {/* Iter 313.j.38 (Renzo, feb-2026) — Diálogo de VB (Aprobar) desde el detalle */}
      <Dialog open={confirmAprobarDialog} onOpenChange={setConfirmAprobarDialog}>
        <DialogContent className="max-w-md" data-testid="modal-confirmar-aprobar-rendicion">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" /> Aprobar Rendición
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <p>
              ¿Confirmas dar <strong>VB</strong> a la rendición <strong>{rendicion?.folio}</strong> por{' '}
              <strong>{fmtCLP(rendicion?.totales?.total_general)}</strong>?
            </p>
            <div className="text-xs text-gray-600">
              Rendidor: <strong>{rendicion?.rendidor?.nombre || '-'}</strong> · Fondo: {rendicion?.medio_pago_nombre || '-'}
            </div>
            {/* Iter 313.j.39: aviso cuando es atajo desde borrador */}
            {rendicion?.estado === 'borrador' && puedeEliminar.puede_aprobar_directo && (
              <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-900" data-testid="hint-atajo-admin">
                <strong>Atajo admin:</strong> vas a autorizar directamente esta rendición sin pasar por el paso &quot;Enviar a VB&quot;.
                Todas las {(puedeEliminar.ocs_por_linea || []).length} OC(s) asociadas ya están aprobadas.
              </div>
            )}
            <label className="flex items-start gap-2 text-sm bg-emerald-50 border border-emerald-200 rounded p-2 cursor-pointer">
              <input
                type="checkbox"
                checked={contabilizarTrasAprobar}
                onChange={(e) => setContabilizarTrasAprobar(e.target.checked)}
                className="mt-0.5"
                data-testid="checkbox-contabilizar-tras-aprobar"
              />
              <span>
                <strong>Contabilizar en Alegra tras aprobar</strong>
                <div className="text-xs text-emerald-800 mt-0.5">
                  Se ejecutará el precheck y luego la creación de Egresos/Bills/Pagos en Alegra.
                  Si falla, la rendición queda en <em>aprobada</em> para reintentar manualmente.
                </div>
              </span>
            </label>
          </div>
          <DialogFooter className="mt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmAprobarDialog(false)}
              disabled={aprobando || integrando || precheckLoading}
              data-testid="btn-cancelar-aprobar"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAprobarDetalle}
              disabled={aprobando || integrando || precheckLoading}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="btn-confirmar-aprobar"
            >
              {(aprobando || integrando || precheckLoading)
                ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                : <CheckCircle className="w-4 h-4 mr-1" />}
              {contabilizarTrasAprobar ? 'Aprobar y Contabilizar' : 'Aprobar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 313.j.38 (Renzo, feb-2026) — Diálogo de Rechazo con motivo */}
      <Dialog open={confirmRechazarDialog} onOpenChange={setConfirmRechazarDialog}>
        <DialogContent className="max-w-md" data-testid="modal-confirmar-rechazar-rendicion">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" /> Rechazar Rendición
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <p>
              ¿Confirmas rechazar la rendición <strong>{rendicion?.folio}</strong>? El rendidor podrá editarla y re-enviarla.
            </p>
            <div>
              <label className="text-xs font-medium text-gray-700">Motivo de rechazo *</label>
              <textarea
                value={motivoRechazo}
                onChange={(e) => setMotivoRechazo(e.target.value)}
                className="w-full mt-1 text-sm border rounded p-2 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-red-300"
                placeholder="Describe brevemente por qué la rechazas (el rendidor lo verá)."
                data-testid="input-motivo-rechazo"
              />
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" size="sm" onClick={() => setConfirmRechazarDialog(false)} disabled={rechazando} data-testid="btn-cancelar-rechazar">
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleRechazarDetalle}
              disabled={rechazando || !(motivoRechazo || '').trim()}
              data-testid="btn-confirmar-rechazar"
            >
              {rechazando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Rechazar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 313.j.25 — Diálogo de confirmación de eliminación */}
      <Dialog open={confirmEliminar} onOpenChange={setConfirmEliminar}>
        <DialogContent className="max-w-md" data-testid="modal-confirmar-eliminar-rendicion">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" /> Eliminar Rendición
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <p>¿Confirmas eliminar la rendición <strong>{rendicion?.folio}</strong>?</p>
            {puedeEliminar.motivo && (
              <div className={`text-xs rounded p-2 ${puedeEliminar.puede_eliminar ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-amber-50 border border-amber-300 text-amber-900'}`}>
                {puedeEliminar.motivo}
              </div>
            )}
            {/* Iter 313.j.31 — Listado de OCs no anuladas con links directos */}
            {(puedeEliminar.ocs_no_anuladas || []).length > 0 && (
              <div className="text-xs border border-amber-300 rounded p-2 bg-amber-50">
                <div className="font-semibold mb-1 text-amber-900">OCs pendientes de anular:</div>
                <ul className="space-y-1">
                  {puedeEliminar.ocs_no_anuladas.map((oc, i) => (
                    <li key={i} className="flex items-center justify-between gap-2">
                      <span className="text-amber-900">Sec.{oc.seccion} · OC <strong>{oc.numero_oc}</strong> · ${(oc.total || 0).toLocaleString('es-CL')}</span>
                      <a
                        href={`/compras/ordenes-compra?open=${oc.numero_oc}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline font-medium inline-flex items-center gap-1"
                        data-testid={`link-oc-anular-${oc.numero_oc}`}
                      >
                        Anular <ExternalLink className="w-3 h-3" />
                      </a>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 text-[10px] text-amber-800">Anula cada OC en su detalle (botón &quot;Anular OC&quot;) y vuelve aquí para eliminar la rendición.</div>
              </div>
            )}
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              ⚠ Esta acción no se puede deshacer. Los registros de OCs anuladas se conservan; solo se elimina la rendición.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmEliminar(false)} disabled={eliminando} data-testid="btn-cancelar-eliminar-rendicion">
              Cancelar
            </Button>
            <Button variant="destructive" size="sm" onClick={handleEliminarRendicion} disabled={eliminando || !puedeEliminar.puede_eliminar} data-testid="btn-confirmar-eliminar-rendicion">
              {eliminando ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
              Eliminar Rendición
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 313.j.27 — Diálogo de confirmación de contabilización en Alegra */}
      <Dialog open={confirmIntegrar} onOpenChange={setConfirmIntegrar}>
        <DialogContent className="max-w-lg" data-testid="modal-confirmar-integrar-alegra">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Zap className="w-5 h-5" /> Contabilizar en Alegra
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <p>Contabilizar la rendición <strong>{rendicion?.folio}</strong> en Alegra creará:</p>
            <ul className="text-xs list-disc pl-5 space-y-1 text-gray-700 dark:text-gray-300">
              <li><b>Sección 1</b> → 1 Egreso categorizado (contra Fondo por Rendir, CC 0997)</li>
              <li><b>Sección 2 FE</b> → 1 Bill + 1 Payment out por cada línea (CC por OT)</li>
              <li><b>Sección 2 NC</b> → 1 Nota de Débito aplicada a la Bill referenciada. Si no existe, se anota como pendiente y se envía email de alerta.</li>
            </ul>
            <div className="text-[11px] bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
              ⚠ Idempotente: si se re-ejecuta, no duplica documentos. Al terminar sin errores, el estado pasa a <b>integrada_alegra</b>.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmIntegrar(false)} disabled={integrando} data-testid="btn-cancelar-integrar">
              Cancelar
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleIntegrarAlegra}
              disabled={integrando}
              data-testid="btn-confirmar-integrar"
            >
              {integrando ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
              Confirmar y Contabilizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 313.j.33 — Diálogo precheck bloqueante (proveedores/cuentas/medios de pago) */}
      <Dialog open={!!precheckResult && !precheckResult.puede_contabilizar} onOpenChange={(o) => !o && setPrecheckResult(null)}>
        <DialogContent className="max-w-lg" data-testid="modal-precheck-alegra">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Info className="w-5 h-5" /> No se puede contabilizar aún
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <p>Antes de contabilizar en Alegra, corrige lo siguiente:</p>
            {(precheckResult?.alertas || []).map((a, i) => (
              <div key={i} className={`text-xs rounded p-2 border ${a.tipo === 'proveedor_no_existe' ? 'bg-red-50 border-red-300 text-red-800' : 'bg-amber-50 border-amber-300 text-amber-900'}`}>
                {a.mensaje}
                {a.rut && (
                  <div className="mt-1">
                    <a href={`/compras/proveedores?rut=${a.rut}`} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline inline-flex items-center gap-1">
                      Ir al maestro <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setPrecheckResult(null)} data-testid="btn-cerrar-precheck">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 313.j.33 — Sección Historial Alegra (Bill/Payment/Egreso con links) */}
      {!isNew && alegraHistorial.length > 0 && (
        <div className="mt-6 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/10 p-4" data-testid="alegra-historial-section">
          <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-300 mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4" /> Historial Alegra ({alegraHistorial.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left border-b border-emerald-200 dark:border-emerald-800">
                <tr>
                  <th className="p-1.5">Tipo</th>
                  <th className="p-1.5">N° Alegra</th>
                  <th className="p-1.5 text-right">Monto</th>
                  <th className="p-1.5">Fecha</th>
                  <th className="p-1.5">Estado</th>
                  <th className="p-1.5">Link</th>
                </tr>
              </thead>
              <tbody>
                {alegraHistorial.map((op, i) => (
                  <tr key={i} className={`border-b border-emerald-100 dark:border-emerald-900 ${op.reversed_at ? 'opacity-60 line-through' : ''}`} data-testid={`alegra-op-${op.tipo}-${op.alegra_id}`}>
                    <td className="p-1.5">{op.tipo_label}</td>
                    <td className="p-1.5 font-mono">#{op.alegra_id}{op.alegra_number ? ` (${op.alegra_number})` : ''}</td>
                    <td className="p-1.5 text-right">${(op.amount || 0).toLocaleString('es-CL')}</td>
                    <td className="p-1.5">{op.created_at ? new Date(op.created_at).toLocaleString('es-CL') : '—'}</td>
                    <td className="p-1.5">
                      {op.reversed_at ? (
                        <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-red-100 text-red-700 border border-red-300">Reversada</span>
                      ) : (
                        <span className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-700 border border-emerald-300">Activa</span>
                      )}
                    </td>
                    <td className="p-1.5">
                      {op.alegra_url ? (
                        <a href={op.alegra_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                          Ver <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {integracionResult && (
        <div className="fixed bottom-4 right-4 max-w-md bg-white dark:bg-slate-900 border-2 border-emerald-400 dark:border-emerald-700 shadow-xl rounded-lg p-4 z-50" data-testid="banner-integracion-result">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-bold text-sm flex items-center gap-2 text-emerald-700">
              <CheckCircle className="w-4 h-4" /> Resultado Alegra
            </h4>
            <Button variant="ghost" size="sm" onClick={() => setIntegracionResult(null)} className="h-6 w-6 p-0">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="text-xs space-y-1">
            {integracionResult.seccion1 && (
              <div><b>Sec.1 Egreso:</b> {integracionResult.seccion1.alegra_payment_id ? `#${integracionResult.seccion1.alegra_payment_id}` : '—'} · {integracionResult.seccion1.categorias || 0} categorías</div>
            )}
            {(integracionResult.seccion2 || []).map((s, i) => (
              <div key={i}><ChevronRight className="w-3 h-3 inline" /> <b>L{s.idx + 1} {s.tipo_doc} {s.folio}:</b> {s.resultado?.alegra_bill_id ? `Bill #${s.resultado.alegra_bill_id}` : s.resultado?.alegra_debit_note_id ? `ND #${s.resultado.alegra_debit_note_id}` : s.resultado?.status || 'ok'}</div>
            ))}
            {(integracionResult.errores || []).length > 0 && (
              <div className="mt-2 pt-2 border-t text-red-700 dark:text-red-400">
                <b>Errores:</b>
                <ul className="list-disc pl-4">
                  {integracionResult.errores.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cabecera de la rendición (tipo Excel) */}
      <div className="bg-white dark:bg-slate-900 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-3">
        {isCreacionEnNombreDeOtro && (
          <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-300 dark:border-blue-800 rounded p-3 mb-2">
            <label className="block text-xs font-bold text-blue-800 dark:text-blue-300 mb-1">
              Crear rendición en nombre de:
            </label>
            <select
              value={fondoSeleccionado}
              onChange={(e) => setFondoSeleccionado(e.target.value)}
              className="w-full h-9 border-2 border-blue-400 rounded px-2 text-sm bg-white dark:bg-slate-900"
              data-testid="select-fondo-gerencia"
            >
              <option value="">— Selecciona el funcionario / fondo —</option>
              {fondosDisponibles.map((f) => (
                <option key={f.codigo} value={f.codigo}>
                  {f.persona_asociada} · {f.nombre}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-blue-700 dark:text-blue-400 mt-1">
              Como Gerencia, puedes crear rendiciones en nombre de cualquiera de los {fondosDisponibles.length} funcionarios con fondo por rendir.
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">RUT</label>
            <div className="font-mono">{rendidorRut || '-'}</div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Nombre</label>
            <div>{rendidorNombre}</div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Fecha</label>
            {isEditable ? (
              <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="h-8 text-sm" data-testid="input-fecha-rendicion" />
            ) : (
              <div>{fecha}</div>
            )}
          </div>
          <div className="md:col-span-4">
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">Medio de Pago (Rendición)</label>
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-blue-600" />
              <span className="font-medium">{medioPagoNombre}</span>
              {(rendicion?.alegra_bank_account_name || fondoActivo?.alegra_bank_account_name) && (
                <span className="text-xs text-gray-500">
                  ↔ Alegra: {rendicion?.alegra_bank_account_name || fondoActivo?.alegra_bank_account_name}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="text-[10px] text-gray-500 flex items-start gap-1">
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>
            Al integrar en Alegra, la cabecera se registrará como <strong>Egreso</strong> contra la cuenta bancaria del fondo.
            El contacto se asocia por el RUT del rendidor.
          </span>
        </div>
      </div>

      {/* Iter 313.j.31 (Renzo, feb-2026) — Banner de modo edición supervisado */}
      {modoSupervisado && !isNew && (
        <div className="mb-4 rounded-lg border-2 border-orange-400 bg-orange-50 dark:bg-orange-900/20 p-3 text-sm" data-testid="banner-modo-supervisado">
          <div className="flex items-start gap-2">
            <Info className="w-5 h-5 text-orange-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-orange-900 dark:text-orange-300">Modo Edición Supervisado activo</div>
              <div className="text-xs text-orange-800 dark:text-orange-400 mt-1">
                Solo se pueden modificar líneas cuya OC esté anulada o sin OC emitida. Las líneas con OC activa se rechazarán al guardar.
              </div>
              {(puedeEliminar.ocs_por_linea || []).filter(o => !o.anulada).length > 0 && (
                <div className="mt-2 text-[11px] text-orange-900 dark:text-orange-300">
                  <b>OCs activas (líneas bloqueadas):</b>
                  <ul className="mt-1 space-y-0.5">
                    {puedeEliminar.ocs_por_linea.filter(o => !o.anulada).map((o, i) => (
                      <li key={i}>
                        Sec.{o.seccion}{o.line_idx != null ? ` L${o.line_idx + 1}` : ''} · OC <b>{o.oc_numero}</b> · <a href={`/compras/ordenes-compra?open=${o.oc_numero}`} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">Anular <ExternalLink className="w-2.5 h-2.5 inline" /></a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sección 1 — Boletas y Comprobantes */}
      <SeccionEditor
        titulo="1.- Gastos con Boletas y Comprobantes"
        subtitulo="TRXB · Bol · GD · CP  →  se agruparán en UNA sola OC al integrar en Alegra"
        lineas={seccion1}
        onUpdate={(idx, field, val) => updateLine(1, idx, field, val)}
        onAdd={() => addLine(1)}
        onRemove={(idx) => removeLine(1, idx)}
        readonly={readonly}
        tiposDoc={tiposDoc.seccion1 || []}
        otItems={otItems}
        cuentasItems={cuentasItems}
        total={totales.total_seccion1}
        testidPrefix="s1"
        seccion={1}
        // Iter 313.j.4 — OC Express agrupada por Sección 1
        ocSeccion1Numero={rendicion?.seccion1_oc_numero}
        onEmitirOCSeccion1={handleEmitirOCSeccion1}
        canEmitirOC={!readonly && !isNew}
        // Iter 313.j.14 — buscar material del maestro
        onOpenMaterialSearch={(idx) => openMaterialSearch('s1', idx)}
      />

      {/* Sección 2 — Facturas y NC */}
      <SeccionEditor
        titulo="2.- Facturas y Notas de Crédito"
        subtitulo="F → 1 OC Express (Contado) por cada factura    ·    NC → NO genera OC, se cruza contra una Factura Previa"
        lineas={seccion2}
        onUpdate={(idx, field, val) => updateLine(2, idx, field, val)}
        onAdd={() => addLine(2)}
        onRemove={(idx) => removeLine(2, idx)}
        readonly={readonly}
        tiposDoc={tiposDoc.seccion2 || []}
        otItems={otItems}
        cuentasItems={cuentasItems}
        total={totales.total_seccion2}
        testidPrefix="s2"
        seccion={2}
        // Iter 313.j.4 — OC Express por cada línea de Sección 2
        onEmitirOCSeccion2={handleEmitirOCSeccion2}
        canEmitirOC={!readonly && !isNew}
        // Iter 313.j.14 — buscar material del maestro
        onOpenMaterialSearch={(idx) => openMaterialSearch('s2', idx)}
      />

      {/* Totales */}
      <div className="bg-blue-50 dark:bg-blue-950/30 border-2 border-blue-300 dark:border-blue-800 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="text-center">
            <div className="text-xs text-gray-600 dark:text-gray-400">Total Sección 1 (Boletas/Comprobantes)</div>
            <div className="text-lg font-bold font-mono" data-testid="total-seccion1">{fmtCLP(totales.total_seccion1)}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-600 dark:text-gray-400">Total Sección 2 (Facturas/NC)</div>
            <div className="text-lg font-bold font-mono" data-testid="total-seccion2">{fmtCLP(totales.total_seccion2)}</div>
          </div>
          <div className="text-center border-l-2 border-blue-400 dark:border-blue-700">
            <div className="text-xs text-gray-600 dark:text-gray-400">TOTAL RENDICIÓN</div>
            <div className="text-2xl font-bold text-blue-700 dark:text-blue-400 font-mono" data-testid="total-general">
              {fmtCLP(totales.total_general)}
            </div>
          </div>
        </div>
      </div>

      {/* Observaciones */}
      <div className="bg-white dark:bg-slate-900 border rounded-lg p-4">
        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Observaciones</label>
        {isEditable ? (
          <textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            rows={2}
            className="w-full border rounded-md px-2 py-1 text-sm bg-transparent"
            placeholder="Notas adicionales sobre la rendición…"
            data-testid="input-observaciones"
          />
        ) : (
          <div className="text-sm text-gray-700 dark:text-gray-300">{observaciones || <em className="text-gray-400">Sin observaciones</em>}</div>
        )}
      </div>

      {/* Documentos abreviados */}
      <div className="text-[10px] text-gray-500 bg-gray-50 dark:bg-slate-800 rounded p-2">
        <strong>Documentos:</strong> FE=Factura Electrónica · B=Boleta · BH=Boleta Honorarios · GD=Guía de Despacho · OC=Orden de Compra · CP=Comprobante de Pago · TRXB=Transferencia contra Boleta · NC=Nota de Crédito
      </div>

      {/* Footer: metadata + estado integración Alegra */}
      {rendicion && (
        <div className="text-[10px] text-gray-500 flex flex-wrap gap-4">
          <span>Creada: {rendicion.created_at?.substring(0, 16).replace('T', ' ')} por {rendicion.created_by}</span>
          {rendicion.aprobada_by && (
            <span className="text-green-600">
              <CheckCircle className="w-3 h-3 inline mr-0.5" />
              VB de {rendicion.aprobada_by} · {rendicion.aprobada_at?.substring(0, 16).replace('T', ' ')}
            </span>
          )}
        </div>
      )}

      {/* Iter 313.j.8 — Modal OC Express Sección 2 (Renzo, feb-2026) */}
      <Dialog
        open={ocExpressModal.open}
        onOpenChange={(o) => { if (!o) setOcExpressModal({ open: false, lineIdx: null, data: null }); }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="modal-oc-express-s2">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" /> OC Express — Sección 2 (Factura)
            </DialogTitle>
          </DialogHeader>
          {ocExpressModal.data && (
            <div className="space-y-4">
              {/* Banner explicativo */}
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-800 dark:text-amber-300">
                  Ajusta OT, tipo de gasto y detalle antes de emitir la OC. La OC se creará con condición <strong>Contado</strong> contra el fondo <strong>{medioPagoNombre}</strong>.
                </div>
              </div>

              {/* Cabecera con RUT / Nombre proveedor */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50 dark:bg-slate-800/50 rounded-md p-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-600 uppercase mb-0.5">RUT proveedor</label>
                  <div className="font-mono text-sm">{ocExpressModal.data.rut || '—'}</div>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-600 uppercase mb-0.5">Nombre proveedor</label>
                  <div className="text-sm truncate">{ocExpressModal.data.rut_nombre || <em className="text-gray-400">Se completará al emitir</em>}</div>
                </div>
              </div>

              {/* Documento + Fecha */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  {/* Iter 313.j.9 (Renzo) — 5 tipos de OC como en el generador normal */}
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Tipo de OC <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {[
                      { key: 'afecta',      label: 'Factura Afecta',      color: 'indigo' },
                      { key: 'honorarios',  label: 'Boleta Honorarios',   color: 'purple' },
                      { key: 'combustible', label: 'Factura Combustible', color: 'orange' },
                      { key: 'exenta',      label: 'Factura Exenta',      color: 'gray'   },
                      { key: 'mixta',       label: 'Factura Mixta',       color: 'teal'   },
                    ].map((t) => {
                      const active = (ocExpressModal.data?.tipo_oc || 'afecta') === t.key;
                      // Colores fijos (Tailwind no interpola nombres dinámicos)
                      const cls = active
                        ? {
                            indigo:  'bg-indigo-600 text-white border-indigo-700',
                            purple:  'bg-purple-600 text-white border-purple-700',
                            orange:  'bg-orange-500 text-white border-orange-600',
                            gray:    'bg-gray-600 text-white border-gray-700',
                            teal:    'bg-teal-600 text-white border-teal-700',
                          }[t.color]
                        : {
                            indigo:  'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50',
                            purple:  'bg-white text-purple-700 border-purple-200 hover:bg-purple-50',
                            orange:  'bg-white text-orange-700 border-orange-200 hover:bg-orange-50',
                            gray:    'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
                            teal:    'bg-white text-teal-700 border-teal-200 hover:bg-teal-50',
                          }[t.color];
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => updateModalField('tipo_oc', t.key)}
                          className={`text-[11px] px-2.5 h-8 rounded-md border transition-colors ${cls}`}
                          data-testid={`modal-oc-tipo-${t.key}`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Fecha</label>
                  <Input
                    type="date"
                    value={ocExpressModal.data.fecha || ''}
                    onChange={(e) => updateModalField('fecha', e.target.value)}
                    className="h-9"
                    data-testid="modal-oc-fecha"
                  />
                </div>
              </div>

              {/* Iter 313.j.26 — Tipo Documento simplificado: FE / BH (NC no genera OC) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Tipo Documento <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={ocExpressModal.data.documento_tipo || 'FE'}
                    onChange={(e) => updateModalField('documento_tipo', e.target.value)}
                    className="w-full h-9 border rounded px-2 text-sm bg-transparent"
                    data-testid="modal-oc-tipo-doc"
                  >
                    <option value="FE">FE — Factura Electrónica</option>
                    <option value="BH">BH — Boleta Honorarios</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Nº Documento <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={ocExpressModal.data.documento_numero || ''}
                    onChange={(e) => updateModalField('documento_numero', e.target.value)}
                    placeholder="ej: 340564"
                    className="h-9 font-mono"
                    data-testid="modal-oc-num-doc"
                  />
                </div>
              </div>

              {/* OT + Tipo de Gasto */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    OT <span className="text-red-500">*</span>
                  </label>
                  <SearchableCombobox
                    items={otItems}
                    value={ocExpressModal.data.ot_numero || ''}
                    onChange={(sel) => updateModalField('ot_numero', sel ? sel.value : '')}
                    placeholder="Buscar OT..."
                    emptyMessage="Sin OTs"
                    testid="modal-oc-ot"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Tipo de Gasto (Cuenta contable) <span className="text-red-500">*</span>
                  </label>
                  <SearchableCombobox
                    items={cuentasItems}
                    value={ocExpressModal.data.tipo_gasto_id || ''}
                    onChange={(sel) => {
                      updateModalField('tipo_gasto_id', sel ? sel.value : '');
                      updateModalField('tipo_gasto_nombre', sel ? sel.label : '');
                    }}
                    placeholder="Buscar cuenta de gasto..."
                    emptyMessage="Sin cuentas"
                    testid="modal-oc-cuenta"
                  />
                </div>
              </div>

              {/* Detalle */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Detalle / Descripción <span className="text-red-500">*</span>
                </label>
                <Input
                  value={ocExpressModal.data.detalle || ''}
                  onChange={(e) => updateModalField('detalle', e.target.value)}
                  placeholder="Ej: Diesel para OT-500"
                  className="h-9"
                  data-testid="modal-oc-detalle"
                />
              </div>

              {/* Iter 313.j.9 — Parámetros de impuestos según tipo_oc */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(ocExpressModal.data.tipo_oc === 'afecta' || ocExpressModal.data.tipo_oc === 'combustible' || ocExpressModal.data.tipo_oc === 'mixta') && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">% IVA</label>
                    <Input
                      type="number" min="0" max="100" step="0.01"
                      value={ocExpressModal.data.iva_porcentaje}
                      onChange={(e) => updateModalField('iva_porcentaje', e.target.value)}
                      className="h-9 font-mono"
                      data-testid="modal-oc-iva-pct"
                    />
                  </div>
                )}
                {ocExpressModal.data.tipo_oc === 'honorarios' && (
                  <div>
                    <label className="block text-xs font-semibold text-purple-700 mb-1">% Retención</label>
                    <Input
                      type="number" min="0" max="100" step="0.01"
                      value={ocExpressModal.data.retencion_porcentaje}
                      onChange={(e) => updateModalField('retencion_porcentaje', e.target.value)}
                      placeholder="15.25"
                      className="h-9 font-mono border-purple-300"
                      data-testid="modal-oc-retencion-pct"
                    />
                    <p className="text-[10px] text-gray-500 mt-0.5">Se descuenta del bruto.</p>
                  </div>
                )}
              </div>

              {/* Otros impuestos (solo Combustible) */}
              {ocExpressModal.data.tipo_oc === 'combustible' && (
                <div className="border border-orange-200 rounded-md p-3 bg-orange-50/40" data-testid="modal-oc-otros-impuestos">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-orange-700">Otros impuestos (IEPD, MEPCO, etc.)</label>
                    <Button
                      type="button" size="sm" variant="outline"
                      onClick={addOtroImpuesto}
                      className="h-7 gap-1 text-[11px] border-orange-300 text-orange-700 hover:bg-orange-100"
                      data-testid="modal-oc-add-otro-imp"
                    >
                      <Plus className="w-3 h-3" /> Agregar
                    </Button>
                  </div>
                  {(ocExpressModal.data.otros_impuestos || []).length === 0 ? (
                    <p className="text-[11px] text-gray-500 italic">Sin impuestos adicionales. Click &quot;Agregar&quot; para añadir uno (ej: IEPD $176.587).</p>
                  ) : (
                    <div className="space-y-1.5">
                      {(ocExpressModal.data.otros_impuestos || []).map((oi, i) => (
                        <div key={i} className="flex gap-2 items-center" data-testid={`modal-oc-otro-imp-row-${i}`}>
                          <Input
                            value={oi.nombre || ''}
                            onChange={(e) => updateOtroImpuesto(i, 'nombre', e.target.value)}
                            placeholder="Ej: IEPD - Imp. Específico Petróleo Diesel"
                            className="h-7 text-xs flex-1"
                            data-testid={`modal-oc-otro-imp-nombre-${i}`}
                          />
                          <NumberInput
                            allowDecimal={false} allowNegative={false}
                            value={oi.monto || ''}
                            onChange={(clean) => updateOtroImpuesto(i, 'monto', clean)}
                            placeholder="Monto $"
                            className="h-7 text-xs w-32 text-right font-mono"
                            data-testid={`modal-oc-otro-imp-monto-${i}`}
                          />
                          <Button
                            type="button" size="icon" variant="ghost"
                            onClick={() => removeOtroImpuesto(i)}
                            className="h-7 w-7 text-red-600 hover:bg-red-50"
                            data-testid={`modal-oc-otro-imp-remove-${i}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Valor + Neto Exento (solo mixta) + Desglose */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                      {ocExpressModal.data.tipo_oc === 'honorarios' ? 'Bruto (antes de retención)' :
                       ocExpressModal.data.tipo_oc === 'mixta' ? 'Neto Afecto (base IVA)' :
                       ocExpressModal.data.tipo_oc === 'combustible' ? 'Total factura (Neto + IVA + Otros)' :
                       ocExpressModal.data.tipo_oc === 'exenta' ? 'Total (sin IVA)' :
                       'Total con IVA'} <span className="text-red-500">*</span>
                    </label>
                    <NumberInput
                      allowDecimal={false} allowNegative={false}
                      value={ocExpressModal.data.valor || ''}
                      onChange={(clean) => updateModalField('valor', parseInt(clean) || 0)}
                      placeholder="0"
                      className="h-9 text-right font-mono font-bold"
                      data-testid="modal-oc-valor"
                    />
                  </div>
                  {ocExpressModal.data.tipo_oc === 'mixta' && (
                    <div>
                      <label className="block text-xs font-semibold text-teal-700 mb-1">Neto Exento (sin IVA) <span className="text-red-500">*</span></label>
                      <NumberInput
                        allowDecimal={false} allowNegative={false}
                        value={ocExpressModal.data.neto_exento || ''}
                        onChange={(clean) => updateModalField('neto_exento', parseInt(clean) || 0)}
                        placeholder="0"
                        className="h-9 text-right font-mono border-teal-300"
                        data-testid="modal-oc-neto-exento"
                      />
                    </div>
                  )}
                </div>
                {/* Desglose Neto/IVA/Retención/Otros/Total en vivo según tipo_oc */}
                <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-md p-2 text-xs space-y-0.5" data-testid="modal-oc-desglose">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{ocExpressModal.data.tipo_oc === 'honorarios' ? 'Bruto:' : 'Neto:'}</span>
                    <span className="font-mono">{fmtCLP(modalTotales.neto)}</span>
                  </div>
                  {(ocExpressModal.data.tipo_oc === 'afecta' || ocExpressModal.data.tipo_oc === 'combustible' || ocExpressModal.data.tipo_oc === 'mixta') && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">IVA ({Number(ocExpressModal.data.iva_porcentaje) || 0}%):</span>
                      <span className="font-mono">{fmtCLP(modalTotales.iva)}</span>
                    </div>
                  )}
                  {ocExpressModal.data.tipo_oc === 'honorarios' && (
                    <div className="flex justify-between text-purple-800">
                      <span>− Retención ({Number(ocExpressModal.data.retencion_porcentaje) || 0}%):</span>
                      <span className="font-mono">−{fmtCLP(modalTotales.retencion)}</span>
                    </div>
                  )}
                  {ocExpressModal.data.tipo_oc === 'combustible' && modalTotales.otros > 0 && (
                    <div className="flex justify-between text-orange-800">
                      <span>Otros impuestos:</span>
                      <span className="font-mono">{fmtCLP(modalTotales.otros)}</span>
                    </div>
                  )}
                  {ocExpressModal.data.tipo_oc === 'mixta' && (
                    <div className="flex justify-between text-teal-800">
                      <span>Neto Exento:</span>
                      <span className="font-mono">{fmtCLP(modalTotales.neto_exento)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-emerald-300 mt-1 pt-1 font-bold text-emerald-800 dark:text-emerald-200">
                    <span>Total OC:</span>
                    <span className="font-mono" data-testid="modal-oc-total">{fmtCLP(modalTotales.total)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setOcExpressModal({ open: false, lineIdx: null, data: null })}
              data-testid="modal-oc-cancelar"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmarEmitirOCSeccion2}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="modal-oc-confirmar"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Confirmar y crear OC Express
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 313.j.14 — Modal Buscar Material del Maestro (Renzo, feb-2026) */}
      <Dialog
        open={materialModal.open}
        onOpenChange={(o) => { if (!o) closeMaterialModal(); }}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="modal-buscar-material">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-blue-600" /> Buscar Material del Maestro
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div className="text-xs text-blue-800 dark:text-blue-300">
                Elige un material para autocompletar la descripción y quedar trazable con el código del maestro.
                <strong> El valor y el tipo de gasto los sigues eligiendo manualmente.</strong>
              </div>
            </div>
            <Input
              value={materialQuery}
              onChange={(e) => setMaterialQuery(e.target.value)}
              placeholder="Buscar por descripción, código o familia..."
              className="h-10"
              autoFocus
              data-testid="modal-material-query"
            />
            <div className="border rounded-md max-h-[45vh] overflow-y-auto" data-testid="modal-material-results">
              {materialLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-500 text-sm gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Buscando...
                </div>
              ) : materialResults.length === 0 ? (
                <div className="py-8 text-center text-gray-400 text-sm">
                  {materialQuery.length < 2 ? 'Escribe al menos 2 caracteres para buscar.' : 'Sin resultados.'}
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">Código</th>
                      <th className="text-left px-2 py-1.5">Descripción</th>
                      <th className="text-left px-2 py-1.5">Familia</th>
                      <th className="text-left px-2 py-1.5">Un.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialResults.map((m) => (
                      <tr
                        key={m.codigo}
                        className="border-t hover:bg-blue-50 dark:hover:bg-blue-950/30 cursor-pointer"
                        onClick={() => selectMaterial(m)}
                        data-testid={`modal-material-row-${m.codigo}`}
                      >
                        <td className="px-2 py-1 font-mono text-[10px] text-blue-700 whitespace-nowrap">{m.codigo}</td>
                        <td className="px-2 py-1">
                          <div className="font-medium">{m.descripcion2 || m.descripcion}</div>
                          {m.descripcion2 && m.descripcion && m.descripcion2 !== m.descripcion && (
                            <div className="text-[10px] text-gray-500 truncate">{m.descripcion}</div>
                          )}
                        </td>
                        <td className="px-2 py-1 text-[10px] text-gray-600 truncate max-w-[140px]">{m.familia_nombre || '—'}</td>
                        <td className="px-2 py-1 text-[10px] text-gray-500">{m.unidad || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeMaterialModal} data-testid="modal-material-cancelar">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// Subcomponente: SeccionEditor (tabla de líneas de gasto)
// ═══════════════════════════════════════════════════════════════════
const SeccionEditor = ({
  titulo, subtitulo, lineas, onUpdate, onAdd, onRemove, readonly, tiposDoc,
  otItems, cuentasItems, total, testidPrefix, seccion,
  // Iter 313.j.4 — OC Express handlers/state
  ocSeccion1Numero, onEmitirOCSeccion1, onEmitirOCSeccion2, canEmitirOC,
  // Iter 313.j.14 — abre modal buscar material desde la columna Detalle
  onOpenMaterialSearch,
}) => {
  const isS1 = seccion === 1;
  const isS2 = seccion === 2;
  // Iter 313.j.4 — Reordenamiento de columnas Sección 2:
  //   S1: Fecha | Tipo | N° Doc | RUT | Tipo Gasto | OT | Detalle | Valor
  //   S2: Fecha | RUT | Tipo | N° Doc | Detalle | Fact.Prev. (solo NC) | OC Express | Valor
  const showFacturaPrevia = isS2;
  const showOCExpressCol = isS2 && canEmitirOC;
  const colCount = isS2 ? (7 + (showOCExpressCol ? 1 : 0)) : 8;

  return (
    <div className="bg-white dark:bg-slate-900 border rounded-lg" data-testid={`seccion-${testidPrefix}`}>
      <div className="border-b p-3 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-sm">{titulo}</h3>
          <div className="text-[10px] text-gray-500">{subtitulo}</div>
        </div>
        {!readonly && (
          <Button size="sm" variant="outline" onClick={onAdd} data-testid={`btn-agregar-${testidPrefix}`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Agregar línea
          </Button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 dark:bg-slate-800 border-b">
            {isS1 ? (
              <tr>
                <th className="text-left py-2 px-2 w-24">Fecha</th>
                <th className="text-left py-2 px-2 w-16">Tipo</th>
                <th className="text-left py-2 px-2 w-20">Nº Doc</th>
                <th className="text-left py-2 px-2 w-56">RUT / Proveedor</th>
                <th className="text-left py-2 px-2 w-48">Tipo Gasto</th>
                <th className="text-left py-2 px-2 w-28">OT</th>
                <th className="text-left py-2 px-2">Detalle</th>
                <th className="text-right py-2 px-2 w-28">Valor $</th>
                {!readonly && <th className="text-center py-2 px-2 w-10"></th>}
              </tr>
            ) : (
              <tr>
                <th className="text-left py-2 px-2 w-24">Fecha</th>
                <th className="text-left py-2 px-2 w-56">RUT / Proveedor</th>
                <th className="text-left py-2 px-2 w-16">Tipo</th>
                <th className="text-left py-2 px-2 w-24">Nº Doc</th>
                <th className="text-left py-2 px-2">Detalle</th>
                <th className="text-left py-2 px-2 w-24">Fact. Prev.</th>
                {showOCExpressCol && <th className="text-center py-2 px-2 w-28">OC Express</th>}
                <th className="text-right py-2 px-2 w-28">Valor $</th>
                {!readonly && <th className="text-center py-2 px-2 w-10"></th>}
              </tr>
            )}
          </thead>
          <tbody>
            {lineas.length === 0 ? (
              <tr>
                <td colSpan={readonly ? colCount : colCount + 1} className="text-center py-6 text-gray-400 text-sm">
                  Sin líneas. {!readonly && 'Haz clic en "Agregar línea".'}
                </td>
              </tr>
            ) : isS1 ? lineas.map((l, idx) => (
              // ═════ SECCIÓN 1 — Boletas y Comprobantes ═════
              <tr key={idx} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`row-${testidPrefix}-${idx}`}>
                <td className="py-1 px-2">
                  {readonly ? l.fecha : (
                    <Input type="date" value={l.fecha || ''} onChange={(e) => onUpdate(idx, 'fecha', e.target.value)} className="h-7 text-xs" />
                  )}
                </td>
                <td className="py-1 px-2">
                  {readonly ? (
                    <span className="font-mono text-[10px]">{l.documento_tipo}</span>
                  ) : (
                    <select value={l.documento_tipo || ''} onChange={(e) => onUpdate(idx, 'documento_tipo', e.target.value)} className="w-full h-7 text-xs border rounded px-1 bg-transparent">
                      {tiposDoc.map((t) => <option key={t.codigo} value={t.codigo}>{t.codigo}</option>)}
                    </select>
                  )}
                </td>
                <td className="py-1 px-2">
                  {readonly ? l.documento_numero : (
                    <Input value={l.documento_numero || ''} onChange={(e) => onUpdate(idx, 'documento_numero', e.target.value)} className="h-7 text-xs font-mono" />
                  )}
                </td>
                <td className="py-1 px-2">
                  {readonly ? (
                    <div>
                      <div className="font-mono">{l.rut}</div>
                      {l.rut_nombre && <div className="text-[10px] text-gray-500 truncate">{l.rut_nombre}</div>}
                    </div>
                  ) : (
                    <RutProveedorCombo value={l.rut} nombre={l.rut_nombre}
                      onSelect={({ rut, nombre }) => { onUpdate(idx, 'rut', rut); onUpdate(idx, 'rut_nombre', nombre); }}
                      testid={`${testidPrefix}-rut-${idx}`} />
                  )}
                </td>
                <td className="py-1 px-2">
                  {readonly ? (l.tipo_gasto_nombre || l.tipo_gasto_id) : (
                    <SearchableCombobox items={cuentasItems}
                      value={l.tipo_gasto_id ? String(l.tipo_gasto_id) : ''}
                      onChange={(sel) => { onUpdate(idx, 'tipo_gasto_id', sel ? sel.value : ''); onUpdate(idx, 'tipo_gasto_nombre', sel ? sel.label : ''); }}
                      placeholder="Cuenta de gasto" emptyMessage="Sin cuentas"
                      testid={`${testidPrefix}-cuenta-${idx}`} compact />
                  )}
                </td>
                <td className="py-1 px-2">
                  {readonly ? (l.ot_numero ? `OT-${l.ot_numero}` : '-') : (
                    <SearchableCombobox items={otItems}
                      value={l.ot_numero ? String(l.ot_numero) : ''}
                      onChange={(sel) => onUpdate(idx, 'ot_numero', sel ? parseInt(sel.value) : null)}
                      placeholder="OT" emptyMessage="Sin OTs"
                      testid={`${testidPrefix}-ot-${idx}`} compact />
                  )}
                </td>
                <td className="py-1 px-2">
                  {readonly ? l.detalle : (
                    <div className="flex items-center gap-1">
                      <Input value={l.detalle || ''} onChange={(e) => onUpdate(idx, 'detalle', e.target.value)} className="h-7 text-xs flex-1" placeholder="Descripción del gasto" />
                      {/* Iter 313.j.14 (Renzo) — buscar material del maestro (opcional) */}
                      {onOpenMaterialSearch && (
                        <Button
                          type="button" size="icon" variant="ghost"
                          onClick={() => onOpenMaterialSearch(idx)}
                          className="h-7 w-7 shrink-0 text-blue-600 hover:bg-blue-50"
                          title="Buscar del maestro de materiales"
                          data-testid={`btn-buscar-material-${testidPrefix}-${idx}`}
                        >
                          <Search className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                  {l.codigo_material && (
                    <div className="text-[9px] text-blue-600 font-mono truncate" title={l.codigo_material}>#{l.codigo_material}</div>
                  )}
                </td>
                <td className="py-1 px-2 text-right">
                  {readonly ? <span className="font-mono">{fmtCLP(l.valor)}</span> : (
                    <NumberInput allowDecimal={false} allowNegative={false} value={l.valor || ''} onChange={(clean) => onUpdate(idx, 'valor', parseInt(clean) || 0)} className="h-7 text-xs text-right font-mono" />
                  )}
                </td>
                {!readonly && (
                  <td className="py-1 px-2 text-center">
                    <Button variant="ghost" size="sm" onClick={() => onRemove(idx)} className="text-red-500 h-6 w-6 p-0" title="Eliminar línea" data-testid={`btn-remove-${testidPrefix}-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                )}
              </tr>
            )) : lineas.map((l, idx) => (
              // ═════ SECCIÓN 2 — Facturas y NC (columnas reordenadas + botón OC Express por fila) ═════
              <tr key={idx} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`row-${testidPrefix}-${idx}`}>
                <td className="py-1 px-2">
                  {readonly ? l.fecha : (
                    <Input type="date" value={l.fecha || ''} onChange={(e) => onUpdate(idx, 'fecha', e.target.value)} className="h-7 text-xs" />
                  )}
                </td>
                <td className="py-1 px-2">
                  {readonly ? (
                    <div>
                      <div className="font-mono">{l.rut}</div>
                      {l.rut_nombre && <div className="text-[10px] text-gray-500 truncate">{l.rut_nombre}</div>}
                    </div>
                  ) : (
                    <RutProveedorCombo value={l.rut} nombre={l.rut_nombre}
                      onSelect={({ rut, nombre }) => { onUpdate(idx, 'rut', rut); onUpdate(idx, 'rut_nombre', nombre); }}
                      testid={`${testidPrefix}-rut-${idx}`} />
                  )}
                </td>
                <td className="py-1 px-2">
                  {readonly ? (
                    <span className="font-mono text-[10px]">{l.documento_tipo}</span>
                  ) : (
                    <select value={l.documento_tipo || ''} onChange={(e) => onUpdate(idx, 'documento_tipo', e.target.value)} className="w-full h-7 text-xs border rounded px-1 bg-transparent">
                      {tiposDoc.map((t) => <option key={t.codigo} value={t.codigo}>{t.codigo}</option>)}
                    </select>
                  )}
                </td>
                <td className="py-1 px-2">
                  {readonly ? l.documento_numero : (
                    <Input value={l.documento_numero || ''} onChange={(e) => onUpdate(idx, 'documento_numero', e.target.value)} className="h-7 text-xs font-mono" />
                  )}
                </td>
                <td className="py-1 px-2">
                  {readonly ? l.detalle : (
                    <div className="flex items-center gap-1">
                      <Input value={l.detalle || ''} onChange={(e) => onUpdate(idx, 'detalle', e.target.value)} className="h-7 text-xs flex-1" placeholder="Descripción del gasto" />
                      {onOpenMaterialSearch && (
                        <Button
                          type="button" size="icon" variant="ghost"
                          onClick={() => onOpenMaterialSearch(idx)}
                          className="h-7 w-7 shrink-0 text-blue-600 hover:bg-blue-50"
                          title="Buscar del maestro de materiales"
                          data-testid={`btn-buscar-material-${testidPrefix}-${idx}`}
                        >
                          <Search className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                  {l.codigo_material && (
                    <div className="text-[9px] text-blue-600 font-mono truncate" title={l.codigo_material}>#{l.codigo_material}</div>
                  )}
                </td>
                {showFacturaPrevia && (
                  <td className="py-1 px-2">
                    {l.documento_tipo === 'NC' ? (
                      readonly ? (
                        <span className="font-mono text-[10px] text-blue-700">{l.factura_previa || '-'}</span>
                      ) : (
                        <Input value={l.factura_previa || ''} onChange={(e) => onUpdate(idx, 'factura_previa', e.target.value)}
                          className="h-7 text-xs font-mono" placeholder="ej: FE 17192"
                          title="Factura que se está creditando con esta NC"
                          data-testid={`${testidPrefix}-factura-previa-${idx}`} />
                      )
                    ) : (
                      <span className="text-gray-300 text-[10px]">—</span>
                    )}
                  </td>
                )}
                {showOCExpressCol && (
                  <td className="py-1 px-2 text-center">
                    {l.documento_tipo === 'NC' ? (
                      <span className="text-[10px] text-gray-400 italic">No aplica</span>
                    ) : l.oc_numero ? (
                      <a
                        href={`/compras/ordenes-compra/${l.oc_numero}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-0.5 text-[10px] px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                        data-testid={`link-oc-${testidPrefix}-${idx}`}
                      >
                        OC {l.oc_numero} <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    ) : (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => onEmitirOCSeccion2 && onEmitirOCSeccion2(idx)}
                        className="h-7 text-[10px] px-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                        data-testid={`btn-emitir-oc-${testidPrefix}-${idx}`}
                      >
                        <Zap className="w-3 h-3 mr-1" /> Emitir OC
                      </Button>
                    )}
                  </td>
                )}
                <td className="py-1 px-2 text-right">
                  {readonly ? <span className="font-mono">{fmtCLP(l.valor)}</span> : (
                    <NumberInput allowDecimal={false} allowNegative={false} value={l.valor || ''} onChange={(clean) => onUpdate(idx, 'valor', parseInt(clean) || 0)} className="h-7 text-xs text-right font-mono" />
                  )}
                </td>
                {!readonly && (
                  <td className="py-1 px-2 text-center">
                    <Button variant="ghost" size="sm" onClick={() => onRemove(idx)} className="text-red-500 h-6 w-6 p-0" title="Eliminar línea" data-testid={`btn-remove-${testidPrefix}-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-50 dark:bg-slate-800 border-t">
            <tr>
              <td colSpan={colCount - 1} className="py-2 px-2 text-right font-semibold">
                Subtotal:
                {/* Iter 313.j.4 — Botón "Emitir OC Express" agrupada al final del subtotal de Sección 1 */}
                {isS1 && canEmitirOC && (
                  <span className="ml-3">
                    {ocSeccion1Numero ? (
                      <a
                        href={`/compras/ordenes-compra/${ocSeccion1Numero}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-0.5 text-[10px] px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                        data-testid="link-oc-s1-emitida"
                      >
                        OC {ocSeccion1Numero} <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    ) : (
                      <Button
                        size="sm" variant="outline"
                        onClick={onEmitirOCSeccion1}
                        className="h-7 text-[10px] px-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                        data-testid="btn-emitir-oc-s1"
                      >
                        <Zap className="w-3 h-3 mr-1" /> Emitir OC Express (agrupada)
                      </Button>
                    )}
                  </span>
                )}
              </td>
              <td className="py-2 px-2 text-right font-bold font-mono" data-testid={`total-${testidPrefix}`}>
                {fmtCLP(total)}
              </td>
              {!readonly && <td></td>}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default RendicionGastosDetail;
